import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, UserCheck, Building, FileText, User, Plus, Pencil, Trash2, BookOpen, Download, Upload, MapPin, Store } from 'lucide-react';
import CustomerReportDialog from '@/components/CustomerReportDialog';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { toast } from 'sonner';
import { API, errorText } from '@/utils/api';

// Available regions and divisions
const REGIONS = ["Jharkhand", "Bihar", "West Bengal", "Odisha", "Assam", "Tripura", "Meghalaya"];
const DIVISIONS = ['Arvind Tresca DTR', 'Arvind Tresca WS', 'GG DTR', 'GG WS', 'TAS'];
const ENTRY_TYPES = [
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'debit_note', label: 'Debit Note' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'opening_balance', label: 'Opening Balance' },
  { value: 'other', label: 'Other' }
];

export default function Customers() {
  const { user, selectedDivision } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterAssignment, setFilterAssignment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'ledger'
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  
  // Ledger state
  const [ledgerData, setLedgerData] = useState({ ledger_entries: [], summary: {} });
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerDivisionFilter, setLedgerDivisionFilter] = useState('all');
  const [addEntryDialog, setAddEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    entry_type: 'adjustment',
    date: new Date().toISOString().split('T')[0],
    division: '',
    description: '',
    debit: '',
    credit: '',
    reference_number: '',
    remarks: ''
  });
  
  // Bulk region assignment state
  const [bulkRegionDialog, setBulkRegionDialog] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Permission checks
  const canEdit = user?.role === 'admin' || user?.role === 'accounts';

  // Account status — changed only via PUT /customers/{id}/status (admin and
  // accounts/finance). Blocking requires a reason; who/when is recorded.
  const STATUS_META = {
    active:  { label: 'Active',  badge: 'bg-green-100 text-green-800 border border-green-200',   panel: 'bg-green-50/60 border-green-200' },
    dormant: { label: 'Dormant', badge: 'bg-yellow-100 text-yellow-800 border border-yellow-200', panel: 'bg-yellow-50/60 border-yellow-200' },
    blocked: { label: 'Blocked', badge: 'bg-orange-100 text-orange-800 border border-orange-300', panel: 'bg-orange-50 border-orange-300' },
    closed:  { label: 'Closed',  badge: 'bg-red-100 text-red-800 border border-red-200',         panel: 'bg-red-50/60 border-red-200' },
  };
  const [statusDraft, setStatusDraft] = useState('active');
  const [statusReason, setStatusReason] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  useEffect(() => {
    if (selectedCustomer) { setStatusDraft(selectedCustomer.status || 'active'); setStatusReason(''); }
  }, [selectedCustomer?.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCustomerStatus = async () => {
    if (!selectedCustomer) return;
    if (statusDraft === 'blocked' && !statusReason.trim()) {
      toast.error('Please give a reason for blocking (e.g. overdues)');
      return;
    }
    setStatusSaving(true);
    try {
      const res = await axios.put(
        `${API}/customers/${selectedCustomer.customer_id}/status`,
        { status: statusDraft, reason: statusReason.trim() },
        { withCredentials: true }
      );
      const f = {
        status: res.data.status_value ?? statusDraft,
        status_reason: res.data.status_reason ?? null,
        status_updated_by: res.data.status_updated_by ?? null,
        status_updated_at: res.data.status_updated_at ?? null,
      };
      f.status = statusDraft;
      setSelectedCustomer(prev => ({ ...prev, ...f }));
      setCustomers(prev => prev.map(c => c.customer_id === selectedCustomer.customer_id ? { ...c, ...f } : c));
      setStatusReason('');
      toast.success(`Account marked ${STATUS_META[statusDraft].label}`);
    } catch (err) {
      toast.error(errorText(err) || 'Failed to update status');
    } finally {
      setStatusSaving(false);
    }
  };
  const isViewOnly = user?.role === 'sales_member';

  // Stores (ship-to locations under one SAP code) — a VIEW on orders,
  // dispatches and targets; billing stays at code level. Declared here with
  // the other sheet state (the old Retailers panel declared its state below
  // the effect that read it and hit a TDZ on first render). Loaded lazily
  // when the sheet opens; reset on customer change. Members see it read-only.
  const [stores, setStores] = useState([]);
  const [storesFy, setStoresFy] = useState('');
  // This FY's sales per division code (net ex-GST, returns netted) — every division.
  const [fySales, setFySales] = useState(null);
  // Location labels: customer_id -> label for the list; per-code rows for the profile.
  const [custLocations, setCustLocations] = useState({});
  const [codeLocations, setCodeLocations] = useState([]);
  const [codeLocEdit, setCodeLocEdit] = useState(null);   // { key, value }
  const [codeLocSaving, setCodeLocSaving] = useState(false);
  // Σ FY sales over the stores listed (net, ex-GST) — the store shares' base.
  const storesTotal = stores.reduce((t, x) => t + (Number(x.fy_sales_value) || 0), 0);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storeEdit, setStoreEdit] = useState(null); // { store_id, field: 'name'|'city'|'state', value } | null
  const [storeSaving, setStoreSaving] = useState(false);
  const fetchStores = useCallback(async (customerId) => {
    if (!customerId) { setStores([]); return; }
    setStoresLoading(true);
    try {
      const res = await axios.get(`${API}/customers/${encodeURIComponent(customerId)}/stores`, { withCredentials: true });
      setStores(Array.isArray(res.data?.stores) ? res.data.stores : []);
      setStoresFy(res.data?.fy || '');
    } catch (err) {
      // No stores (or a profile out of scope) just hides the panel.
      setStores([]);
    } finally {
      setStoresLoading(false);
    }
  }, []);
  useEffect(() => {
    setFySales(null);
    if (!sheetOpen || !selectedCustomer?.customer_id) return undefined;
    let alive = true;
    axios.get(`${API}/customers/${encodeURIComponent(selectedCustomer.customer_id)}/fy-sales`, { withCredentials: true })
      .then(res => { if (alive) setFySales(res.data || null); })
      .catch(() => { if (alive) setFySales(null); });
    return () => { alive = false; };
  }, [sheetOpen, selectedCustomer?.customer_id]);
  const inr = (v) => `₹${Math.round(Number(v) || 0).toLocaleString('en-IN')}`;
  const fetchCustLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customers/locations`, { withCredentials: true });
      setCustLocations(res.data?.locations || {});
    } catch { /* labels are a convenience; the list works without them */ }
  }, []);
  useEffect(() => { fetchCustLocations(); }, [fetchCustLocations]);
  const fetchCodeLocations = useCallback(async (customerId) => {
    if (!customerId) { setCodeLocations([]); return; }
    try {
      const res = await axios.get(`${API}/customers/${encodeURIComponent(customerId)}/locations`, { withCredentials: true });
      setCodeLocations(res.data?.codes || []);
    } catch { setCodeLocations([]); }
  }, []);
  useEffect(() => {
    setCodeLocations([]);
    setCodeLocEdit(null);
    if (sheetOpen && selectedCustomer?.customer_id) fetchCodeLocations(selectedCustomer.customer_id);
  }, [sheetOpen, selectedCustomer?.customer_id, fetchCodeLocations]);
  const nck = (c) => String(c || '').trim().replace(/\.0$/, '').replace(/^0+/, '');
  const codeLocFor = (div) => codeLocations.find(r => r.division === div?.division && nck(r.customer_code) === nck(div?.customer_code)) || null;
  const saveCodeLocation = async (div, value) => {
    if (!selectedCustomer?.customer_id) return;
    setCodeLocSaving(true);
    try {
      await axios.put(`${API}/customers/${encodeURIComponent(selectedCustomer.customer_id)}/locations`,
        { division: div.division, customer_code: div.customer_code, location: (value ?? '').trim() },
        { withCredentials: true });
      setCodeLocEdit(null);
      toast.success((value ?? '').trim() ? 'Location saved' : 'Location reset to the default');
      fetchCodeLocations(selectedCustomer.customer_id);
      fetchCustLocations();
    } catch (err) {
      toast.error(errorText(err) || 'Failed to save location');
    } finally {
      setCodeLocSaving(false);
    }
  };
  useEffect(() => {
    setStores([]);
    setStoreEdit(null);
    if (sheetOpen && selectedCustomer?.customer_id) fetchStores(selectedCustomer.customer_id);
  }, [sheetOpen, selectedCustomer?.customer_id, fetchStores]);

  // One field at a time: name / city / state via PUT .../stores/{store_id}.
  // An empty name reverts the store to the name the dispatch file gave it.
  const saveStoreField = async (store, field, value) => {
    if (!selectedCustomer?.customer_id) return;
    setStoreSaving(true);
    try {
      const res = await axios.put(
        `${API}/customers/${encodeURIComponent(selectedCustomer.customer_id)}/stores/${encodeURIComponent(store.store_id)}`,
        { [field]: (value ?? '').trim() },
        { withCredentials: true }
      );
      const updated = res.data?.store || res.data || {};
      setStores(prev => prev.map(s => s.store_id === store.store_id ? { ...s, ...updated } : s));
      setStoreEdit(null);
      toast.success(field === 'name' && !(value ?? '').trim() ? 'Store name reverted to the file name'
        : field === 'location' && !(value ?? '').trim() ? 'Location reset to the default' : 'Store updated');
      if (field === 'location') { fetchCustLocations(); fetchCodeLocations(selectedCustomer.customer_id); }
    } catch (err) {
      toast.error(errorText(err) || 'Failed to update store');
    } finally {
      setStoreSaving(false);
    }
  };
  const toggleStoreActive = async (store, active) => {
    if (!selectedCustomer?.customer_id) return;
    setStoreSaving(true);
    try {
      const res = await axios.put(
        `${API}/customers/${encodeURIComponent(selectedCustomer.customer_id)}/stores/${encodeURIComponent(store.store_id)}`,
        { active },
        { withCredentials: true }
      );
      const updated = res.data?.store || res.data || {};
      setStores(prev => prev.map(s => s.store_id === store.store_id ? { ...s, ...updated, active } : s));
    } catch (err) {
      toast.error(errorText(err) || 'Failed to update store');
    } finally {
      setStoreSaving(false);
    }
  };

  const fetchCustomers = useCallback(async () => {
    try {
      const params = selectedDivision && selectedDivision !== 'all' ? `?division=${encodeURIComponent(selectedDivision)}` : '';
      const response = await axios.get(`${API}/customers${params}`, {
        withCredentials: true
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [selectedDivision]);
  
  const downloadRegionTemplate = async () => {
    try {
      const divParam = selectedDivision && selectedDivision !== 'all' ? `?division=${encodeURIComponent(selectedDivision)}` : '';
      const response = await axios.get(`${API}/customers/region-template${divParam}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Region_Template_${selectedDivision || 'All'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const uploadBulkRegions = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const response = await axios.post(`${API}/customers/bulk-assign-regions`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(response.data);
      if (response.data.updated > 0) {
        toast.success(`Updated ${response.data.updated} customers`);
        fetchCustomers();
      } else {
        toast.info('No customers were updated');
      }
    } catch (error) {
      toast.error(errorText(error) || 'Upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        withCredentials: true
      });
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };
  
  const fetchLedger = useCallback(async (customerId) => {
    if (!customerId) return;
    setLedgerLoading(true);
    try {
      const divParam = ledgerDivisionFilter && ledgerDivisionFilter !== 'all' ? `?division=${encodeURIComponent(ledgerDivisionFilter)}` : '';
      const response = await axios.get(`${API}/customers/${customerId}/ledger${divParam}`, {
        withCredentials: true
      });
      setLedgerData(response.data);
    } catch (error) {
      console.error('Failed to fetch ledger:', error);
      toast.error('Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  }, [ledgerDivisionFilter]);
  
  useEffect(() => {
    fetchCustomers();
    fetchEmployees();
  }, [fetchCustomers]);
  
  useEffect(() => {
    if (activeTab === 'ledger' && selectedCustomer?.customer_id) {
      fetchLedger(selectedCustomer.customer_id);
    }
  }, [activeTab, selectedCustomer?.customer_id, fetchLedger]);

  const openCustomerProfile = (customer) => {
    setSelectedCustomer({ ...customer });
    setActiveTab('profile');
    setLedgerData({ ledger_entries: [], summary: {} });
    setLedgerDivisionFilter('all');
    setSheetOpen(true);
  };

  const updateSelectedField = (key, value) => {
    setSelectedCustomer((prev) => ({ ...prev, [key]: value }));
  };

  const handleAssignEmployee = async (customerId, employeeId) => {
    try {
      await axios.put(`${API}/customers/${customerId}/assign-employee?employee_id=${employeeId || ''}`, {}, {
        withCredentials: true
      });
      toast.success(employeeId ? 'Employee assigned' : 'Set to Head Office');
      fetchCustomers();
      if (selectedCustomer?.customer_id === customerId) {
        setSelectedCustomer(prev => ({ ...prev, assigned_employee_id: employeeId || null }));
      }
    } catch (error) {
      toast.error(errorText(error) || 'Failed to assign employee');
    }
  };

  const saveCustomerProfile = async () => {
    if (!selectedCustomer?.customer_id) return;
    setSaving(true);
    try {
      const payload = {
        customer_code: selectedCustomer.customer_code ?? null,
        name: selectedCustomer.name,
        customer_type: selectedCustomer.customer_type,
        region: selectedCustomer.region,
        credit_period: selectedCustomer.credit_period,
        credit_limit: selectedCustomer.credit_limit ?? null,
        contact_person: selectedCustomer.contact_person ?? null,
        contact_email: selectedCustomer.contact_email ?? null,
        contact_phone: selectedCustomer.contact_phone ?? null,
        mobile_number: selectedCustomer.mobile_number ?? null,
        address: selectedCustomer.address ?? null,
        gst_number: selectedCustomer.gst_number ?? null,
        birthday: selectedCustomer.birthday ?? null,
        anniversary: selectedCustomer.anniversary ?? null,
        remarks: selectedCustomer.remarks ?? null,
      };

      await axios.put(`${API}/customers/${selectedCustomer.customer_id}`, payload, {
        withCredentials: true,
      });
      
      if (selectedCustomer.divisions && selectedCustomer.divisions.length > 0) {
        await axios.put(`${API}/customers/${selectedCustomer.customer_id}/divisions`, 
          selectedCustomer.divisions,
          { withCredentials: true }
        );
      }

      toast.success('Customer profile saved');
      setCustomers((prev) => prev.map((c) => (c.customer_id === selectedCustomer.customer_id ? { ...c, ...payload } : c)));
      setSheetOpen(false);
    } catch (error) {
      console.error('Failed to save customer:', error);
      toast.error('Failed to save customer profile');
    } finally {
      setSaving(false);
    }
  };
  
  // Ledger Entry handlers
  const resetEntryForm = () => {
    setEntryForm({
      entry_type: 'adjustment',
      date: new Date().toISOString().split('T')[0],
      division: ledgerDivisionFilter !== 'all' ? ledgerDivisionFilter : '',
      description: '',
      debit: '',
      credit: '',
      reference_number: '',
      remarks: ''
    });
    setEditingEntry(null);
  };
  
  const openAddEntryDialog = () => {
    resetEntryForm();
    setAddEntryDialog(true);
  };
  
  const openEditEntryDialog = (entry) => {
    if (entry.is_system) {
      toast.error('System-generated entries cannot be edited');
      return;
    }
    setEditingEntry(entry);
    setEntryForm({
      entry_type: entry.entry_type || 'adjustment',
      date: entry.date || '',
      division: entry.division || '',
      description: entry.description || '',
      debit: entry.debit || '',
      credit: entry.credit || '',
      reference_number: entry.reference_number || '',
      remarks: entry.remarks || ''
    });
    setAddEntryDialog(true);
  };
  
  const saveEntry = async () => {
    if (!entryForm.description) {
      toast.error('Description is required');
      return;
    }
    if (!entryForm.date) {
      toast.error('Date is required');
      return;
    }
    if (!entryForm.debit && !entryForm.credit) {
      toast.error('Enter either debit or credit amount');
      return;
    }
    
    try {
      const payload = {
        entry_type: entryForm.entry_type,
        date: entryForm.date,
        division: entryForm.division || null,
        description: entryForm.description,
        debit: parseFloat(entryForm.debit) || 0,
        credit: parseFloat(entryForm.credit) || 0,
        reference_number: entryForm.reference_number || null,
        remarks: entryForm.remarks || null
      };
      
      if (editingEntry) {
        await axios.put(`${API}/customers/${selectedCustomer.customer_id}/ledger/${editingEntry.entry_id}`, payload, {
          withCredentials: true
        });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/customers/${selectedCustomer.customer_id}/ledger`, payload, {
          withCredentials: true
        });
        toast.success('Entry added');
      }
      
      setAddEntryDialog(false);
      resetEntryForm();
      fetchLedger(selectedCustomer.customer_id);
    } catch (error) {
      console.error('Failed to save entry:', error);
      toast.error(errorText(error) || 'Failed to save entry');
    }
  };
  
  const deleteEntry = async (entry) => {
    if (entry.is_system) {
      toast.error('System-generated entries cannot be deleted');
      return;
    }
    
    try {
      await axios.delete(`${API}/customers/${selectedCustomer.customer_id}/ledger/${entry.entry_id}`, {
        withCredentials: true
      });
      toast.success('Entry deleted');
      fetchLedger(selectedCustomer.customer_id);
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error(errorText(error) || 'Failed to delete entry');
    }
  };
  
  // Helper to check if customer has any employee assignment
  const hasAnyAssignment = (customer) => {
    if (customer.assigned_employee_id) return true;
    if (customer.divisions && customer.divisions.length > 0) {
      return customer.divisions.some(div => div.assigned_employee_id);
    }
    return false;
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (customer.customer_code || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = filterRegion === 'all' || (filterRegion === 'Unknown' ? (!customer.region || customer.region === 'Unknown') : customer.region === filterRegion);
    const matchesAssignment = filterAssignment === 'all' || 
                              (filterAssignment === 'assigned' && hasAnyAssignment(customer)) ||
                              (filterAssignment === 'head_office' && !hasAnyAssignment(customer));
    const matchesStatus = filterStatus === 'all' || (customer.status || 'active') === filterStatus;
    return matchesSearch && matchesRegion && matchesAssignment && matchesStatus;
  });
  
  const regions = [...new Set(customers.map(c => c.region))];
  
  const getEmployeeName = (employeeId) => {
    if (!employeeId) return null;
    const emp = employees.find(e => e.employee_id === employeeId);
    return emp?.name || 'Unknown';
  };
  
  const getTypeColor = (type) => {
    const colors = {
      retailer: 'bg-blue-100 text-blue-800',
      wholesaler: 'bg-amber-100 text-amber-800',
      distributor: 'bg-green-100 text-green-800',
      brand_store: 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // The channel is a property of the DIVISION, not the profile: a WS code is
  // wholesale by definition. Every importer used to stamp "retailer" on each
  // profile it created, so wholesalers wore retailer badges by default.
  // Derive from the division entries; fall back to the stored type only when
  // a profile has no division entries at all.
  const channelBadges = (customer) => {
    const divs = customer.divisions || [];
    const kinds = [...new Set(divs.map(d => (d.division || '').toUpperCase().endsWith('WS') ? 'wholesaler'
                                            : (d.division || '').toUpperCase().endsWith('DTR') ? 'retailer' : null).filter(Boolean))];
    return kinds.length ? kinds : [customer.customer_type].filter(Boolean);
  };
  
  const getEntryTypeStyle = (type) => {
    const styles = {
      invoice: 'bg-blue-100 text-blue-800',
      payment: 'bg-green-100 text-green-800',
      credit_note: 'bg-amber-100 text-amber-800',
      debit_note: 'bg-red-100 text-red-800',
      cash_discount: 'bg-purple-100 text-purple-800',
      adjustment: 'bg-slate-100 text-slate-800',
      opening_balance: 'bg-indigo-100 text-indigo-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return styles[type] || styles.other;
  };
  
  const formatCurrency = (val) => {
    if (!val || val === 0) return '-';
    return `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold text-foreground mb-2">
          Customers
        </h1>
        <p className="text-muted-foreground">Manage your customer database</p>
      </div>
      
      {/* Filters */}
      <Card className="mb-6 border-border/60 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
              <Input
                data-testid="customer-search-input"
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-slate-200 focus:border-primary focus:ring-primary/20 rounded-lg h-11"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-xs text-muted-foreground self-center mr-2">Region:</span>
            <Button
              data-testid="filter-all"
              variant={filterRegion === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterRegion('all')}
              className="rounded-full"
              size="sm"
            >
              All
            </Button>
            {regions.filter(r => r && r !== 'Unknown').map(region => (
              <Button
                key={region}
                data-testid={`filter-${region}`}
                variant={filterRegion === region ? 'default' : 'outline'}
                onClick={() => setFilterRegion(region)}
                className="rounded-full"
                size="sm"
              >
                {region}
              </Button>
            ))}
            {customers.some(c => !c.region || c.region === 'Unknown') && (
              <Button
                variant={filterRegion === 'Unknown' ? 'destructive' : 'outline'}
                onClick={() => setFilterRegion('Unknown')}
                className="rounded-full"
                size="sm"
              >
                <MapPin size={14} className="mr-1" />
                Unknown ({customers.filter(c => !c.region || c.region === 'Unknown').length})
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" className="rounded-full ml-auto" onClick={() => { setBulkRegionDialog(true); setBulkResult(null); setBulkFile(null); }}>
                <Upload size={14} className="mr-1" /> Bulk Assign Regions
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground self-center mr-2">Assignment:</span>
            <Button
              variant={filterAssignment === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterAssignment('all')}
              className="rounded-full"
              size="sm"
            >
              All
            </Button>
            <Button
              variant={filterAssignment === 'assigned' ? 'default' : 'outline'}
              onClick={() => setFilterAssignment('assigned')}
              className="rounded-full"
              size="sm"
            >
              <UserCheck size={14} className="mr-1" />
              Assigned ({customers.filter(c => hasAnyAssignment(c)).length})
            </Button>
            <Button
              variant={filterAssignment === 'head_office' ? 'default' : 'outline'}
              onClick={() => setFilterAssignment('head_office')}
              className="rounded-full"
              size="sm"
            >
              <Building size={14} className="mr-1" />
              Head Office ({customers.filter(c => !hasAnyAssignment(c)).length})
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground self-center mr-2">Status:</span>
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active' },
              { key: 'dormant', label: 'Dormant' },
              { key: 'blocked', label: 'Blocked' },
              { key: 'closed', label: 'Closed' },
            ].map(({ key, label }) => {
              const n = key === 'all' ? null : customers.filter(c => (c.status || 'active') === key).length;
              const warn = key === 'blocked' && n > 0 && filterStatus !== 'blocked';
              return (
                <Button
                  key={key}
                  variant={filterStatus === key ? 'default' : 'outline'}
                  onClick={() => setFilterStatus(key)}
                  className={`rounded-full ${warn ? 'border-orange-300 text-orange-700 hover:bg-orange-50' : ''}`}
                  size="sm"
                  data-testid={`status-filter-${key}`}
                >
                  {label}{n !== null ? ` (${n})` : ''}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Customer Profile Sidebar */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-[700px] sm:max-w-[700px] overflow-y-auto p-0">
          <SheetHeader className="p-6 pb-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <SheetTitle className="flex items-center gap-2">
                  <User size={20} />
                  {selectedCustomer?.name || 'Customer'}
                </SheetTitle>
                <SheetDescription>
                  View and manage customer details
                </SheetDescription>
              </div>
              {selectedCustomer && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  onClick={() => setReportDialogOpen(true)}
                  title="View this customer’s dispatches and orders for any period or set of months — or download PDF / Excel to share"
                  data-testid="open-customer-report"
                >
                  <FileText size={14} className="mr-1" />
                  Dispatches &amp; orders
                </Button>
              )}
            </div>
          </SheetHeader>

          {/* Tabs */}
          <div className="border-b px-6">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'profile' 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-profile"
              >
                <User size={16} className="inline mr-2" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('ledger')}
                className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'ledger' 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-ledger"
              >
                <BookOpen size={16} className="inline mr-2" />
                Ledger
              </button>
            </div>
          </div>

          {!selectedCustomer ? (
            <div className="p-6 text-sm text-muted-foreground">No customer selected</div>
          ) : activeTab === 'profile' ? (
            /* Profile Tab Content */
            <div className="p-6 space-y-6">
              {/* Account status — prominent; admin/finance can change it */}
              {(() => {
                const cur = STATUS_META[selectedCustomer.status || 'active'] ? (selectedCustomer.status || 'active') : 'active';
                const meta = STATUS_META[cur];
                const changed = statusDraft !== cur;
                return (
                  <div className={`p-4 rounded-lg border ${meta.panel}`} data-testid="customer-status-panel">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Label className="font-semibold">Account status</Label>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                          <Badge className={`${meta.badge} text-sm px-2.5`}>{meta.label}</Badge>
                          {selectedCustomer.status_reason && (
                            <span className="text-sm text-foreground/80">{selectedCustomer.status_reason}</span>
                          )}
                        </div>
                        {selectedCustomer.status_updated_by && (
                          <p className="text-[11px] text-muted-foreground mt-1.5">
                            Changed by {selectedCustomer.status_updated_by}
                            {selectedCustomer.status_updated_at ? ` on ${new Date(selectedCustomer.status_updated_at).toLocaleDateString('en-IN')}` : ''}
                          </p>
                        )}
                      </div>
                      {canEdit ? (
                        <Select value={statusDraft} onValueChange={setStatusDraft}>
                          <SelectTrigger className="w-[130px] bg-white" data-testid="customer-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="dormant">Dormant</SelectItem>
                            <SelectItem value="blocked">Blocked</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">Admin / finance can change</span>
                      )}
                    </div>
                    {canEdit && changed && (
                      <div className="mt-3 flex items-center gap-2">
                        <Input
                          placeholder={statusDraft === 'blocked' ? 'Reason for blocking (required) — e.g. overdues beyond 120 days' : 'Reason (optional)'}
                          value={statusReason}
                          onChange={e => setStatusReason(e.target.value)}
                          className="bg-white"
                          data-testid="customer-status-reason"
                        />
                        <Button size="sm" onClick={saveCustomerStatus} disabled={statusSaving}>
                          {statusSaving ? 'Saving…' : `Mark ${STATUS_META[statusDraft].label}`}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setStatusDraft(cur); setStatusReason(''); }}>Cancel</Button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Division-wise Employee Assignment */}
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-indigo-900 font-semibold">Division Assignments</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-indigo-600"
                    onClick={() => {
                      const currentDivisions = selectedCustomer.divisions || [];
                      const usedDivisions = currentDivisions.map(d => d.division);
                      const availableDivision = DIVISIONS.find(d => !usedDivisions.includes(d));
                      if (availableDivision) {
                        updateSelectedField('divisions', [...currentDivisions, { division: availableDivision, customer_code: '', assigned_employee_id: null }]);
                      }
                    }}
                  >
                    + Add Division
                  </Button>
                </div>
                <p className="text-xs text-indigo-700 mb-3">
                  Assign different customer codes and employees for each division this customer works with.
                </p>
                
                {(!selectedCustomer.divisions || selectedCustomer.divisions.length === 0) ? (
                  <div className="p-3 bg-white rounded border border-dashed border-indigo-300 text-center">
                    <p className="text-sm text-muted-foreground">No divisions assigned yet.</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => updateSelectedField('divisions', [{ division: 'Arvind Tresca DTR', customer_code: selectedCustomer.customer_code || '', assigned_employee_id: null }])}
                    >
                      Add First Division
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedCustomer.divisions.map((div, idx) => (
                      <div key={idx} className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <Select
                            value={div.division}
                            onValueChange={(v) => {
                              const updated = [...selectedCustomer.divisions];
                              updated[idx] = { ...updated[idx], division: v };
                              updateSelectedField('divisions', updated);
                            }}
                          >
                            <SelectTrigger className="w-48 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DIVISIONS.map(d => (
                                <SelectItem key={d} value={d}>{d}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                            onClick={() => {
                              const updated = selectedCustomer.divisions.filter((_, i) => i !== idx);
                              updateSelectedField('divisions', updated);
                            }}
                          >
                            ×
                          </Button>
                        </div>
                        {(() => {
                          const lr = codeLocFor(div);
                          if (!lr) return null;
                          const key = `${div.division}|${div.customer_code}`;
                          if (lr.stores > 1) {
                            return <p className="text-xs text-sky-800 mb-2">Location: <span className="font-medium">{lr.stores} stores</span> <span className="text-muted-foreground">— set each store's location in the Stores panel below.</span></p>;
                          }
                          return codeLocEdit?.key === key ? (
                            <div className="flex items-center gap-1 mb-2">
                              <span className="text-xs text-sky-800 w-16">Location</span>
                              <Input autoFocus maxLength={60} className="h-7 text-xs flex-1" value={codeLocEdit.value} disabled={codeLocSaving}
                                placeholder={lr.default_label || 'e.g. Bistupur, Jamshedpur'}
                                onChange={e => setCodeLocEdit(prev => ({ ...prev, value: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') saveCodeLocation(div, codeLocEdit.value); if (e.key === 'Escape') setCodeLocEdit(null); }} />
                              <Button size="sm" className="h-7 px-2 text-xs" disabled={codeLocSaving} onClick={() => saveCodeLocation(div, codeLocEdit.value)}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={codeLocSaving} onClick={() => setCodeLocEdit(null)}>Cancel</Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 mb-2 group text-xs">
                              <span className="text-sky-800 w-16">Location</span>
                              <span className={lr.label ? 'font-medium text-sky-900' : 'text-muted-foreground italic'}>{lr.label || 'not set'}</span>
                              {!lr.location && lr.label && <span className="text-[10px] text-muted-foreground">(from the dispatch city)</span>}
                              {!isViewOnly && (
                                <button type="button" className="opacity-50 group-hover:opacity-100 text-sky-700" title="Edit location (leave blank to go back to the default)"
                                  onClick={() => setCodeLocEdit({ key, value: lr.location || '' })}>
                                  <Pencil size={11} />
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Customer Code</Label>
                            <Input
                              value={div.customer_code || ''}
                              onChange={(e) => {
                                const updated = [...selectedCustomer.divisions];
                                updated[idx] = { ...updated[idx], customer_code: e.target.value };
                                updateSelectedField('divisions', updated);
                              }}
                              placeholder="e.g., 4011611"
                              className="mt-1 h-8 text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Assigned Employee</Label>
                            <Select
                              value={div.assigned_employee_id || 'head_office'}
                              onValueChange={(v) => {
                                const updated = [...selectedCustomer.divisions];
                                updated[idx] = { ...updated[idx], assigned_employee_id: v === 'head_office' ? null : v };
                                updateSelectedField('divisions', updated);
                              }}
                            >
                              <SelectTrigger className="mt-1 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="head_office">Head Office</SelectItem>
                                {employees.filter(e => e.is_active !== false).map((emp) => (
                                  <SelectItem key={emp.employee_id} value={emp.employee_id}>
                                    {emp.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {/* Base Discount - admin only */}
                        {user?.role === 'admin' ? (
                          <div className="mt-2">
                            <Label className="text-xs text-amber-700">Base Discount % <span className="text-muted-foreground">(WS / Special DTR)</span></Label>
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              step={0.5}
                              value={div.base_discount_pct || 0}
                              onChange={(e) => {
                                const updated = [...selectedCustomer.divisions];
                                updated[idx] = { ...updated[idx], base_discount_pct: parseFloat(e.target.value) || 0 };
                                updateSelectedField('divisions', updated);
                              }}
                              placeholder="0"
                              className="mt-1 h-8 text-sm w-32"
                            />
                          </div>
                        ) : (div.base_discount_pct > 0 && (
                          <div className="mt-2">
                            <Label className="text-xs text-amber-700">Base Discount %</Label>
                            <p className="text-sm font-medium text-amber-700 mt-1">{div.base_discount_pct}%</p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sales this FY per division code — same table style as the TAS Stores
                  panel. Net ex-GST, goods returns netted, free samples excluded
                  (the Sales Targets basis). */}
              {fySales && (fySales.divisions || []).length > 0 && (
                <div className="p-4 rounded-lg border bg-indigo-50 border-indigo-200">
                  <Label className="font-semibold text-indigo-900">Sales FY {fySales.fy}</Label>
                  <p className="text-xs text-indigo-700 mt-0.5 mb-3">Net of GST, goods returns deducted, free samples excluded — the same basis as Sales Targets.</p>
                  <div className="bg-white rounded border border-indigo-200 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[10px]">Division</TableHead>
                          <TableHead className="text-[10px] w-[110px]">SAP code</TableHead>
                          <TableHead className="text-[10px] text-right w-[120px]">Sales FY {fySales.fy}</TableHead>
                          <TableHead className="text-[10px] text-right w-[60px]">Share</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fySales.divisions.map((r) => (
                          <TableRow key={`${r.division}-${r.customer_code}`}>
                            <TableCell className="text-xs py-1.5">{r.division}</TableCell>
                            <TableCell className="text-xs font-mono py-1.5">{r.customer_code}</TableCell>
                            <TableCell className="text-xs text-right py-1.5 tabular-nums font-medium">{r.fy_sales_value ? inr(r.fy_sales_value) : '—'}</TableCell>
                            <TableCell className="text-[10px] text-right py-1.5 tabular-nums text-muted-foreground">
                              {fySales.fy_sales_value > 0 && r.fy_sales_value ? `${Math.round((r.fy_sales_value / fySales.fy_sales_value) * 1000) / 10}%` : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {fySales.divisions.length > 1 && (
                          <TableRow className="bg-indigo-50/60">
                            <TableCell colSpan={2} className="text-[10px] font-semibold text-right py-1.5 text-indigo-900">Account total, FY {fySales.fy}</TableCell>
                            <TableCell className="text-xs text-right py-1.5 tabular-nums font-semibold text-indigo-900">{fySales.fy_sales_value ? inr(fySales.fy_sales_value) : '—'}</TableCell>
                            <TableCell className="text-[10px] text-right py-1.5 text-muted-foreground">{fySales.fy_sales_value ? '100%' : ''}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Stores — ship-to locations the SAP file carries under this
                  customer's code(s). Shown only when the master has at least
                  one. Names come from the dispatch file until someone renames
                  them here (clear the name to go back to the file name). A
                  ship-to equal to the sold-to code is the "Head office" row. */}
              {(storesLoading || stores.length > 0) && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-amber-900 font-semibold flex items-center gap-1.5">
                      <Store size={14} className="text-amber-700" /> Stores
                      {stores.length > 0 && <span className="text-xs font-normal text-amber-700">({stores.length})</span>}
                    </Label>
                    {storesLoading && <span className="text-xs text-amber-700">Loading…</span>}
                  </div>
                  <p className="text-xs text-amber-700 mb-3">
                    Ship-to locations from the dispatch and order files. Billing, ledger and outstanding stay on the SAP code; stores only split orders, dispatches and targets.
                    {isViewOnly ? ' View only.' : ' Click the pencil to rename; leave the name blank to go back to the file name.'}
                  </p>
                  {stores.length > 0 && (
                    <div className="bg-white rounded border border-amber-200 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-[10px] w-[90px]">Ship-to</TableHead>
                            <TableHead className="text-[10px]">Name</TableHead>
                            <TableHead className="text-[10px] w-[140px]" title="Short place label shown next to the customer name across the app">Location</TableHead>
                            <TableHead className="text-[10px] w-[110px]">City</TableHead>
                            <TableHead className="text-[10px] w-[110px]">State</TableHead>
                            <TableHead className="text-[10px] w-[70px]">Division</TableHead>
                            <TableHead className="text-[10px] text-right w-[110px]" title="Net sales this financial year, ex-GST, goods returns netted, free samples excluded (same basis as Sales Targets)">Sales FY {storesFy}</TableHead>
                            <TableHead className="text-[10px] text-right w-[55px]" title="Share of the account's sales this FY">Share</TableHead>
                            <TableHead className="text-[10px] w-[60px] text-center">Active</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stores.map((s) => {
                            const editing = (field) => storeEdit?.store_id === s.store_id && storeEdit?.field === field;
                            const cell = (field, display, placeholder) => editing(field) ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  autoFocus
                                  value={storeEdit.value}
                                  maxLength={120}
                                  placeholder={placeholder}
                                  className="h-7 text-xs"
                                  disabled={storeSaving}
                                  onChange={(e) => setStoreEdit(prev => ({ ...prev, value: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveStoreField(s, field, storeEdit.value);
                                    if (e.key === 'Escape') setStoreEdit(null);
                                  }}
                                />
                                <Button size="sm" className="h-7 px-2 text-xs" disabled={storeSaving} onClick={() => saveStoreField(s, field, storeEdit.value)}>Save</Button>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={storeSaving} onClick={() => setStoreEdit(null)}>Cancel</Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 group">
                                <span className={`text-xs ${display ? '' : 'text-muted-foreground italic'}`}>{display || placeholder}</span>
                                {!isViewOnly && (
                                  <button type="button" className="opacity-40 group-hover:opacity-100 text-amber-700" title={`Edit ${field}`}
                                    onClick={() => setStoreEdit({ store_id: s.store_id, field, value: field === 'name' ? (s.name_source === 'manual' ? s.name || '' : '') : (s[field] || '') })}>
                                    <Pencil size={11} />
                                  </button>
                                )}
                              </div>
                            );
                            return (
                              <TableRow key={s.store_id} className={s.active === false ? 'opacity-60' : ''}>
                                <TableCell className="text-xs font-mono py-1.5">{s.ship_to_code}</TableCell>
                                <TableCell className="py-1.5">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {cell('name', s.display_name || s.name, s.file_name || (s.is_head ? 'Store not specified' : s.ship_to_code))}
                                    {s.is_head && (s.head_is_store
                                      ? <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-800 bg-amber-50" title="Goods are shipped to the SAP code itself — a real store">Head store</Badge>
                                      : <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500 bg-slate-50" title="Orders placed on the SAP code without naming a store. Not a store: no target, not counted as a store.">Not a store</Badge>)}
                                    {s.name_source === 'manual' && s.file_name && s.file_name !== s.name && (
                                      <span className="text-[10px] text-muted-foreground" title="Name on the dispatch file">file: {s.file_name}</span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="py-1.5">{cell('location', s.location || s.location_label, s.location_label || '—')}</TableCell>
                                <TableCell className="py-1.5">{cell('city', s.city, '—')}</TableCell>
                                <TableCell className="py-1.5">{cell('state', s.state, '—')}</TableCell>
                                <TableCell className="text-[10px] py-1.5">{s.division}</TableCell>
                                <TableCell className="text-xs text-right py-1.5 tabular-nums font-medium">
                                  {s.fy_sales_value ? `₹${Math.round(s.fy_sales_value).toLocaleString('en-IN')}` : '—'}
                                </TableCell>
                                <TableCell className="text-[10px] text-right py-1.5 tabular-nums text-muted-foreground">
                                  {storesTotal > 0 && s.fy_sales_value ? `${Math.round((s.fy_sales_value / storesTotal) * 1000) / 10}%` : '—'}
                                </TableCell>
                                <TableCell className="py-1.5 text-center">
                                  <Switch checked={s.active !== false} disabled={isViewOnly || storeSaving}
                                    onCheckedChange={(v) => toggleStoreActive(s, v)} className="scale-75" />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {stores.length > 1 && (
                            <TableRow className="bg-amber-50/60">
                              <TableCell colSpan={6} className="text-[10px] font-semibold text-right py-1.5 text-amber-900">Account total, FY {storesFy}</TableCell>
                              <TableCell className="text-xs text-right py-1.5 tabular-nums font-semibold text-amber-900">
                                {storesTotal ? `₹${Math.round(storesTotal).toLocaleString('en-IN')}` : '—'}
                              </TableCell>
                              <TableCell className="text-[10px] text-right py-1.5 text-muted-foreground">{storesTotal ? '100%' : ''}</TableCell>
                              <TableCell />
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}

              {/* Legacy single assignment */}
              {selectedCustomer.customer_code && (!selectedCustomer.divisions || selectedCustomer.divisions.length === 0) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="text-amber-800">
                    <strong>Legacy Code:</strong> {selectedCustomer.customer_code}
                    {selectedCustomer.assigned_employee_id && (
                      <span className="ml-2">• Assigned to: {employees.find(e => e.employee_id === selectedCustomer.assigned_employee_id)?.name || 'Unknown'}</span>
                    )}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">Click &quot;Add First Division&quot; above to migrate to the new division-based assignment.</p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={selectedCustomer.name ?? ''}
                    onChange={(e) => updateSelectedField('name', e.target.value)}
                    placeholder="Customer name"
                    className="mt-1"
                    disabled={isViewOnly}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Region</Label>
                    <Select 
                      value={selectedCustomer.region ?? ''} 
                      onValueChange={(value) => updateSelectedField('region', value)}
                    >
                      <SelectTrigger className="mt-1" data-testid="customer-region-select">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map((region) => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Credit Period (days)</Label>
                    <Input
                      type="number"
                      value={selectedCustomer.credit_period ?? 45}
                      onChange={(e) => updateSelectedField('credit_period', Number(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Credit Limit</Label>
                  <Input
                    type="number"
                    value={selectedCustomer.credit_limit ?? ''}
                    onChange={(e) => updateSelectedField('credit_limit', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder="e.g., 500000"
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Contact Person</Label>
                    <Input
                      value={selectedCustomer.contact_person ?? ''}
                      onChange={(e) => updateSelectedField('contact_person', e.target.value)}
                      placeholder="Contact person"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Mobile Number</Label>
                    <Input
                      value={selectedCustomer.mobile_number ?? ''}
                      onChange={(e) => updateSelectedField('mobile_number', e.target.value)}
                      placeholder="Mobile"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={selectedCustomer.contact_email ?? ''}
                      onChange={(e) => updateSelectedField('contact_email', e.target.value)}
                      placeholder="Email"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={selectedCustomer.contact_phone ?? ''}
                      onChange={(e) => updateSelectedField('contact_phone', e.target.value)}
                      placeholder="Phone"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>GST Number</Label>
                  <Input
                    value={selectedCustomer.gst_number ?? ''}
                    onChange={(e) => updateSelectedField('gst_number', e.target.value)}
                    placeholder="GSTIN"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Address</Label>
                  <Input
                    value={selectedCustomer.address ?? ''}
                    onChange={(e) => updateSelectedField('address', e.target.value)}
                    placeholder="Address"
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Birthday</Label>
                    <Input
                      type="date"
                      value={selectedCustomer.birthday ?? ''}
                      onChange={(e) => updateSelectedField('birthday', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Anniversary</Label>
                    <Input
                      type="date"
                      value={selectedCustomer.anniversary ?? ''}
                      onChange={(e) => updateSelectedField('anniversary', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Remarks</Label>
                  <Input
                    value={selectedCustomer.remarks ?? ''}
                    onChange={(e) => updateSelectedField('remarks', e.target.value)}
                    placeholder="Internal notes"
                    className="mt-1"
                  />
                </div>
              </div>

              {canEdit && (
                <div className="pt-2">
                  <Button onClick={saveCustomerProfile} disabled={saving} className="w-full rounded-full">
                    {saving ? 'Saving…' : 'Save Profile'}
                  </Button>
                </div>
              )}
              
              {isViewOnly && (
                <div className="pt-2 text-center">
                  <p className="text-sm text-muted-foreground">View only mode - Contact admin to make changes</p>
                </div>
              )}
            </div>
          ) : (
            /* Ledger Tab Content */
            <div className="p-6 space-y-4">
              {/* Ledger Header with filter and add button */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Label className="text-sm whitespace-nowrap">Filter by Division:</Label>
                  <Select value={ledgerDivisionFilter} onValueChange={setLedgerDivisionFilter}>
                    <SelectTrigger className="w-48 h-9">
                      <SelectValue placeholder="All Divisions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Divisions</SelectItem>
                      {DIVISIONS.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  {/* Export buttons — customer-facing artefacts, admin/accounts
                      only (backend enforces the same). Viewing stays open. */}
                  {/* One Excel/PDF pair PER DIVISION, never a merged file. A
                      statement is a per-division document: a merged one
                      (Arvind invoices followed by a tail of GG residuals from
                      a different report) went out to a party before this. The
                      backend refuses a division-less export for multi-division
                      customers, so this is the only way to get one. Divisions
                      come from the ledger rows themselves, so a GG code with
                      no profile entry still gets its own download.
                      Shown to every role: access is enforced by scope on the
                      backend (division + region/assignment), not by role. */}
                  {(() => {
                    const fromLedger = [...new Set((ledgerData?.ledger_entries || []).map(e => e.division).filter(Boolean))];
                    const fromProfile = (selectedCustomer.divisions || []).map(d => d.division).filter(Boolean);
                    const all = fromLedger.length ? fromLedger : [...new Set(fromProfile)];
                    const divs = ledgerDivisionFilter !== 'all' ? all.filter(d => d === ledgerDivisionFilter) : all;
                    const exp = (div, kind) => window.open(
                      `${API}/customers/${selectedCustomer.customer_id}/ledger/export/${kind}?division=${encodeURIComponent(div)}`, '_blank');
                    return divs.map(div => (
                      <div key={div} className="flex items-center gap-1 rounded-md border border-border/60 pl-2 pr-1 py-0.5" title={`Ledger for ${div} only`}>
                        <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">{div}</span>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => exp(div, 'excel')} data-testid={`export-excel-${div}`}>
                          <Download size={13} className="mr-1" />Excel
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => exp(div, 'pdf')} data-testid={`export-pdf-${div}`}>
                          <Download size={13} className="mr-1" />PDF
                        </Button>
                      </div>
                    ));
                  })()}
                  {canEdit && (
                    <Button size="sm" onClick={openAddEntryDialog} data-testid="add-ledger-entry-btn">
                      <Plus size={16} className="mr-1" />
                      Add Entry
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 mb-1">Total Debit</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(ledgerData.summary?.total_debit)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-600 mb-1">Total Credit</p>
                  <p className="text-lg font-bold text-green-800">{formatCurrency(ledgerData.summary?.total_credit)}</p>
                </div>
                <div className={`p-3 rounded-lg border ${(ledgerData.summary?.closing_balance || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <p className="text-xs text-slate-600 mb-1">Balance</p>
                  <p className={`text-lg font-bold ${(ledgerData.summary?.closing_balance || 0) > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                    {formatCurrency(Math.abs(ledgerData.summary?.closing_balance || 0))}
                    {(ledgerData.summary?.closing_balance || 0) > 0 ? ' Dr' : (ledgerData.summary?.closing_balance || 0) < 0 ? ' Cr' : ''}
                  </p>
                </div>
              </div>
              
              {/* Ledger Table */}
              {ledgerLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : ledgerData.ledger_entries?.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText size={40} className="mx-auto mb-3 opacity-50" />
                  <p>No ledger entries found</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-50">
                        <TableRow>
                          <TableHead className="w-[90px]">Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-[80px]">Division</TableHead>
                          <TableHead className="text-right w-[100px]">Debit</TableHead>
                          <TableHead className="text-right w-[100px]">Credit</TableHead>
                          <TableHead className="text-right w-[100px]">Balance</TableHead>
                          {canEdit && <TableHead className="w-[60px]"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ledgerData.ledger_entries.map((entry, idx) => (
                          <TableRow 
                            key={entry.entry_id || idx}
                            className={entry.entry_type === 'payment' ? 'bg-green-50/50' : entry.entry_type === 'credit_note' ? 'bg-amber-50/50' : ''}
                          >
                            <TableCell className="text-xs">{entry.date}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge className={`${getEntryTypeStyle(entry.entry_type)} text-[10px] px-1.5 py-0`}>
                                  {entry.entry_type?.replace('_', ' ')}
                                </Badge>
                                <span className="text-sm">{entry.description}</span>
                              </div>
                              {entry.remarks && <p className="text-xs text-muted-foreground mt-0.5">{entry.remarks}</p>}
                            </TableCell>
                            <TableCell className="text-xs">{entry.division?.split(' ').pop() || '-'}</TableCell>
                            <TableCell className="text-right text-sm font-medium text-blue-700">
                              {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-green-700">
                              {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                            </TableCell>
                            <TableCell className={`text-right text-sm font-medium ${entry.running_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {formatCurrency(Math.abs(entry.running_balance))}
                              {entry.running_balance > 0 ? ' Dr' : entry.running_balance < 0 ? ' Cr' : ''}
                            </TableCell>
                            {canEdit && (
                              <TableCell>
                                {!entry.is_system && (
                                  <div className="flex gap-1">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 w-7 p-0"
                                      onClick={() => openEditEntryDialog(entry)}
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                      onClick={() => deleteEntry(entry)}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Customer Report dialog */}
      <CustomerReportDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        customer={selectedCustomer ? {
          customer_id: selectedCustomer.customer_id,
          customer_code: selectedCustomer.customer_code,
          name: selectedCustomer.name,
        } : null}
        defaultPeriod="range"
        defaultDivision={selectedDivision !== 'all' ? selectedDivision : undefined}
      />

      {/* Add/Edit Entry Dialog */}
      <Dialog open={addEntryDialog} onOpenChange={setAddEntryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Ledger Entry' : 'Add Ledger Entry'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Entry Type</Label>
                <Select value={entryForm.entry_type} onValueChange={(v) => setEntryForm(prev => ({ ...prev, entry_type: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTRY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={entryForm.date}
                  onChange={(e) => setEntryForm(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Division</Label>
              <Select value={entryForm.division || 'none'} onValueChange={(v) => setEntryForm(prev => ({ ...prev, division: v === 'none' ? '' : v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select division" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Division</SelectItem>
                  {DIVISIONS.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Input
                value={entryForm.description}
                onChange={(e) => setEntryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Debit Amount</Label>
                <Input
                  type="number"
                  value={entryForm.debit}
                  onChange={(e) => setEntryForm(prev => ({ ...prev, debit: e.target.value, credit: e.target.value ? '' : prev.credit }))}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Credit Amount</Label>
                <Input
                  type="number"
                  value={entryForm.credit}
                  onChange={(e) => setEntryForm(prev => ({ ...prev, credit: e.target.value, debit: e.target.value ? '' : prev.debit }))}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Reference Number</Label>
              <Input
                value={entryForm.reference_number}
                onChange={(e) => setEntryForm(prev => ({ ...prev, reference_number: e.target.value }))}
                placeholder="e.g., CN-001"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Remarks</Label>
              <Input
                value={entryForm.remarks}
                onChange={(e) => setEntryForm(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Additional notes"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEntryDialog(false)}>Cancel</Button>
            <Button onClick={saveEntry}>{editingEntry ? 'Update' : 'Add'} Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Region Assignment Dialog */}
      <Dialog open={bulkRegionDialog} onOpenChange={setBulkRegionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Assign Regions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Download the template, fill in the <strong>New Region</strong> column, and upload it back. Regions set here are permanent and won't be overwritten by future imports.
            </p>
            <Button variant="outline" className="w-full" onClick={downloadRegionTemplate}>
              <Download size={14} className="mr-2" /> Download Template {selectedDivision && selectedDivision !== 'all' ? `(${selectedDivision})` : '(All Divisions)'}
            </Button>
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Upload Completed File</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                className="mt-1"
                onChange={(e) => { setBulkFile(e.target.files[0] || null); setBulkResult(null); }}
              />
            </div>
            {bulkResult && (
              <div className={`rounded-lg p-3 text-sm ${bulkResult.updated > 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                <p className="font-semibold">{bulkResult.message}</p>
                <div className="flex gap-4 mt-1 text-xs">
                  <span className="text-green-700">Updated: {bulkResult.updated}</span>
                  <span className="text-red-600">Not found: {bulkResult.not_found}</span>
                  <span className="text-muted-foreground">Skipped: {bulkResult.skipped}</span>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600 max-h-20 overflow-y-auto">
                    {bulkResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRegionDialog(false)}>Close</Button>
            <Button onClick={uploadBulkRegions} disabled={!bulkFile || bulkUploading}>
              {bulkUploading ? 'Uploading...' : 'Upload & Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCustomers.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-muted-foreground">No customers found</p>
          </div>
        ) : (
          filteredCustomers.map((customer) => (
            <Card
              key={customer.customer_id}
              data-testid={`customer-card-${customer.customer_id}`}
              className="border-border/60 shadow-sm hover:shadow-md transition-all cursor-pointer"
              onClick={() => openCustomerProfile(customer)}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-xl font-heading font-semibold flex items-start justify-between">
                  <span>
                    {customer.name}
                    {custLocations[customer.customer_id] && (
                      <span className="block text-xs font-normal text-sky-700 mt-0.5">{custLocations[customer.customer_id]}</span>
                    )}
                  </span>
                  <span className="flex gap-1">
                    {channelBadges(customer).map(k => (
                      <Badge key={k} className={getTypeColor(k)}>{k.replace('_', ' ')}</Badge>
                    ))}
                    {customer.status && customer.status !== 'active' && STATUS_META[customer.status] && (
                      <Badge className={STATUS_META[customer.status].badge} title={customer.status_reason || ''}>
                        {STATUS_META[customer.status].label}
                      </Badge>
                    )}
                  </span>
                </CardTitle>
                {/* Division Badges with Employee Assignment */}
                <div className="mt-1 flex flex-wrap gap-1">
                  {customer.divisions && customer.divisions.length > 0 ? (
                    customer.divisions.map((div, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded">
                        {div.division?.split(' ').slice(-1)[0]}
                        {div.customer_code && <span className="text-indigo-600">({div.customer_code})</span>}
                        {div.assigned_employee_id && (
                          <span className="text-blue-700 font-medium">• {getEmployeeName(div.assigned_employee_id)}</span>
                        )}
                      </span>
                    ))
                  ) : customer.assigned_employee_id ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                      <UserCheck size={12} />
                      {getEmployeeName(customer.assigned_employee_id)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                      <Building size={12} />
                      Head Office
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm font-body">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Region:</span>
                    <span className="font-medium text-foreground">{customer.region}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Credit Period:</span>
                    <span className="font-medium text-foreground">{customer.credit_period} days</span>
                  </div>
                  {customer.contact_email && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <span className="font-medium text-foreground truncate ml-2">{customer.contact_email}</span>
                    </div>
                  )}
                  {customer.contact_phone && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone:</span>
                      <span className="font-medium text-foreground">{customer.contact_phone}</span>
                    </div>
                  )}
                  {customer.remarks && (
                    <div className="mt-3 p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Remarks:</p>
                      <p className="text-sm text-foreground">{customer.remarks}</p>
                    </div>
                  )}
                  {customer.cash_discount_policy > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs text-green-700 font-semibold mb-1">Cash Discount Policy:</p>
                      <p className="text-sm text-green-800">
                        {customer.cash_discount_policy}% discount if paid within {customer.cd_payment_days} days
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
